import { AxiosError, AxiosResponse } from 'axios';
import { ApiErrorResponse, QboApiErrorResponse, createLogEntry, LogEntry, LogMessageType, getQboErrorMessage } from "../types/AllTypesExports";
import LoggingStorage from './LoggingStorage';

/**
 * Low-cardinality classification of a tokenization failure. Kept to a small, stable
 * set of values so it can be used as a clean breakdown dimension in PostHog insights.
 */
export type PaymentTokenizeOutcome =
    | 'network_no_response' // transport blocked before any response: CORS/CSP/DNS/firewall/ad-block/offline
    | 'timeout'             // request started but exceeded the configured timeout
    | 'cancelled'           // request aborted (navigation away / user cancel)
    | 'http_error'          // processor responded with a non-2xx status
    | 'unknown';

/**
 * Structured processor validation error extracted from a tokenize response. These are
 * static error strings and field names (e.g. "card.cvc"), never cardholder data.
 */
interface ProcessorErrorDetail {
    processorErrorCode?: string;
    processorErrorDetail?: string;
    processorErrorMessage?: string;
    processorMoreInfo?: string;
    processorErrorType?: string;
    processorInfoLink?: string;
}

/**
 * Derived, PCI-safe shape of the card input at failure time. Counts, brand, booleans only.
 */
interface CardInputShape {
    cardBrand?: string;
    cardNumberLength?: number;
    cvcLength?: number;
    expectedCvcLength?: number;
    cvcLengthMismatch?: boolean;
    expirationProvided?: boolean;
}

const MAX_LOG_FIELD_LENGTH = 200;
const MAX_RESPONSE_JSON_LENGTH = 2000;

function truncateLogField(value?: string | number | null): string | undefined {
    if (value === undefined || value === null) {
        return undefined;
    }
    const str = String(value);
    return str.length > MAX_LOG_FIELD_LENGTH ? str.substring(0, MAX_LOG_FIELD_LENGTH) : str;
}

function serializeResponseData(data: any): string | undefined {
    if (data === undefined || data === null) {
        return undefined;
    }
    try {
        const json = JSON.stringify(data);
        return json.length > MAX_RESPONSE_JSON_LENGTH
            ? json.substring(0, MAX_RESPONSE_JSON_LENGTH)
            : json;
    }
    catch {
        return undefined;
    }
}

/**
 * Normalizes the structured error from either processor's tokenize response.
 * QuickBooks Payments: { errors: [{ code, detail, message, moreInfo, type, infoLink }] }
 * Fiserv CardSecure:   { message, errorcode }
 */
function extractProcessorError(data: any): ProcessorErrorDetail {
    const result: ProcessorErrorDetail = {};
    if (!data) {
        return result;
    }

    if (Array.isArray(data.errors) && data.errors.length > 0) {
        const error = data.errors[0] || {};
        result.processorErrorCode = truncateLogField(error.code);
        result.processorErrorDetail = truncateLogField(error.detail);
        result.processorErrorMessage = truncateLogField(error.message);
        result.processorMoreInfo = truncateLogField(error.moreInfo);
        result.processorErrorType = truncateLogField(error.type);
        result.processorInfoLink = truncateLogField(error.infoLink);
        return result;
    }

    if (data.errorcode !== undefined || typeof data.message === 'string') {
        result.processorErrorCode = truncateLogField(data.errorcode);
        result.processorErrorMessage = truncateLogField(data.message);
        return result;
    }

    return result;
}

/**
 * Metadata for a failed payment tokenization. PCI: every field must be transport metadata,
 * a low-cardinality count/brand/boolean, or a static processor error string. Never PAN,
 * CVV, expiry, routing/account numbers, or names.
 *
 * Pass `responseData` from the axios error and NoviLogging will extract structured
 * processor fields plus a truncated JSON snapshot for PostHog (matching what was
 * previously only visible in session-replay console logs).
 */
export interface PaymentFailureContext extends ProcessorErrorDetail, CardInputShape {
    processor: string;
    paymentType: 'creditCard' | 'ach';
    code?: string;
    message?: string;
    hasResponse?: boolean;
    httpStatus?: number | null;
    tokenizeUrl?: string;
    processorUrlConfigured?: boolean;
    responseData?: any;
}

export interface TokenizeOutcomeContext {
    code?: string;
    message?: string;
    hasResponse?: boolean;
    httpStatus?: number | null;
}

/**
 * Maps transport metadata to a small, stable outcome enum for PostHog breakdowns and
 * user-facing tokenize error messaging. Shared so UI and analytics use the same rule.
 */
export function classifyTokenizeOutcome(ctx: TokenizeOutcomeContext): PaymentTokenizeOutcome {
    if (ctx.hasResponse && ctx.httpStatus) {
        return 'http_error';
    }
    const code = (ctx.code || '').toUpperCase();
    if (code === 'ECONNABORTED' || /timeout/i.test(ctx.message || '')) {
        return 'timeout';
    }
    if (code === 'ERR_CANCELED') {
        return 'cancelled';
    }
    if (ctx.hasResponse === false) {
        return 'network_no_response';
    }
    return 'unknown';
}

export function classifyTokenizeOutcomeFromAxiosError(error: AxiosError): PaymentTokenizeOutcome {
    return classifyTokenizeOutcome({
        code: error?.code,
        message: error?.message,
        hasResponse: !!error?.response,
        httpStatus: error?.response?.status ?? null,
    });
}

class NoviLoggingService {
    toConsole(...args: any[]) {
        if (!this._hasArgs(args)) {
            return;
        }
        console.log("\n");
        args.forEach(item => console.log(item));
    }

    /**
     * Logs a simple information message to the console and interested third parties
     * @param msg Message to display
     * @param context Object that contains contextual metadata around this message.
     */
    info<TObj>(msg: string, context?: TObj) {
        const entry = createLogEntry(<LogEntry>{ message: msg, logMessageType: LogMessageType.Info });
        if (context) {
            entry.customData = context;
        }
        console.log("\n");
        console.log(entry.message);
        if (entry.customData) {
            console.log(entry.customData);
        }
        LoggingStorage.storeLogEntry(entry);
    }

    /**
     * Logs a simple warning message (and potential context object) to the console and to interested third parties.
     * @param msg Message to display
     * @param context Object that contains contextual metadata around this warning.
     */
    warn<TObj>(msg: string, context?: TObj) {
        const entry = createLogEntry(<LogEntry>{ message: msg, logMessageType: LogMessageType.Warning });
        if (context) {
            entry.customData = context;
        }
        console.log("\n");
        if (!entry.customData) {
            console.warn(entry.message, entry.customData);
        }
        else {
            console.warn(entry.message);
        }
        LoggingStorage.storeLogEntry(entry);
    }

    /**
     * Logs a simple error message (and potential context object) to the console and to interested third parties.
     * @param msg Message to display
     * @param context Object that contains contextual metadata around this error.
     */
    error<TObj>(msg: string, context?: TObj) {
        const entry = createLogEntry(<LogEntry>{ message: msg, logMessageType: LogMessageType.Error });
        console.log("\n");
        if (context) {
            entry.customData = { context };
            console.error(entry.message, entry.customData);
        }
        else {
            console.error(entry.message);
        }
        LoggingStorage.storeLogEntry(entry);
    }

    /**
     * Logs an exception to the console and to interested third parties.
     * @param error Javascript Error object. You can create a custom error by calling `new Error('some custom message').stack`. Reference https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error
     * @param context Object that contains contextual metadata around this error.
     */
    exception<TObj>(error: Error, context?: TObj) {
        try {
            const entry = createLogEntry(<LogEntry>{ message: error.message, logMessageType: LogMessageType.Exception });
            if (context) {
                entry.customData = { context };
            }
            else {
                entry.customData = { context: 'no custom data' };
            }
            console.log("\n");
            console.error(entry.message, {error, customData: entry.customData});
            LoggingStorage.storeLogEntry(entry);
        } 
        catch (e) {
            // Swallow to avoid unnecessary exceptions
            console.error(e);
        }
    }

    /**
     * Logs a Debug message type to the console (only)
     * @param args Arguments to display on the console.
     */
    debug(...args: any[]) {
        if (!this._hasArgs(args)) {
            return;
        }
        console.log("\n");
        args.forEach(item => console.debug(item));
    }

    /**
     * Logs a Trace message to the console (only)
     * @param args Arguments to log to the console
     */
    trace(...args: any[]) {
        if (!this._hasArgs(args)) {
            return;
        }
        console.log("\n");
        args.forEach(item => console.trace(item));
    }

    /**
     * Takes an Axios error from a call to a Novi API and logs it
     * @param axiosError
     */
    noviApiError(axiosError: AxiosError) {
        const axiosResponse = <AxiosResponse<ApiErrorResponse>>axiosError.response;
        // A transport-level failure (CORS/CSP/DNS/firewall/abort/timeout) has no
        // response. Reading .data off it would throw and mask the real cause.
        if (!axiosResponse) {
            this.error(axiosError?.message || 'Network error: no response received from the API', {
                code: axiosError?.code,
                url: axiosError?.config?.url,
            });
            return axiosResponse;
        }
        this.error(axiosResponse.data.message, axiosResponse.data);
        return axiosResponse;
    }

    /**
     * Takes an Axios error from a call to the QBO API and logs it
     * @param axiosError
     */
    qboApiError(axiosError: AxiosError) {
        const axiosResponse = <AxiosResponse<QboApiErrorResponse>>axiosError.response;
        // A transport-level failure (CORS/CSP/DNS/firewall/abort/timeout) has no
        // response. Reading .data off it would throw and mask the real cause.
        if (!axiosResponse) {
            this.error(axiosError?.message || 'Network error: no response received from the API', {
                code: axiosError?.code,
                url: axiosError?.config?.url,
            });
            return axiosResponse;
        }
        this.error(getQboErrorMessage(axiosResponse.data), axiosResponse.data);
        return axiosResponse;
    }

    /**
     * Logs a payment tokenization failure to the console/session replay AND emits a
     * queryable PostHog event ('payment_tokenize_failed') so the failure shows up in
     * insights/dashboards, not only in a session recording.
     *
     * PCI: the context must only ever contain transport-level metadata. Never pass the
     * card/ACH payload, PAN, CVV, or routing/account numbers.
     * @param context Transport-only metadata describing the failed tokenization.
     */
    capturePaymentFailure(context: PaymentFailureContext) {
        try {
            const processorError = extractProcessorError(context.responseData);
            const processorResponseData = serializeResponseData(context.responseData);
            const enrichedContext = {
                ...context,
                ...processorError,
                processorResponseData,
            };

            this.error('Payment tokenization failed', enrichedContext);

            // PostHog is feature-flagged and absent when running locally, so null-guard it.
            // $browser/$os/$device_type/$geoip_*/$current_url/$host/$session_id are attached
            // automatically by PostHog, so only payment-specific metadata is added here.
            window.posthog?.capture('payment_tokenize_failed', {
                processor: context.processor,
                paymentType: context.paymentType,
                outcome: classifyTokenizeOutcome(context),
                code: context.code,
                message: context.message,
                hasResponse: context.hasResponse,
                httpStatus: context.httpStatus,
                tokenizeUrl: context.tokenizeUrl,
                processorUrlConfigured: context.processorUrlConfigured,
                // Structured processor error (validation metadata, never cardholder data).
                processorErrorCode: processorError.processorErrorCode,
                processorErrorDetail: processorError.processorErrorDetail,
                processorErrorMessage: processorError.processorErrorMessage,
                processorMoreInfo: processorError.processorMoreInfo,
                processorErrorType: processorError.processorErrorType,
                processorInfoLink: processorError.processorInfoLink,
                // Truncated JSON of the full processor response (matches console responseData).
                processorResponseData,
                // Derived card-input shape (counts/brand/booleans only).
                cardBrand: context.cardBrand,
                cardNumberLength: context.cardNumberLength,
                cvcLength: context.cvcLength,
                expectedCvcLength: context.expectedCvcLength,
                cvcLengthMismatch: context.cvcLengthMismatch,
                expirationProvided: context.expirationProvided,
            });
        }
        catch (e) {
            // Never let logging break the payment flow or skip the user-facing error.
            console.error(e);
        }
    }

    private _hasArgs(...args: any[]) {
        return args && args.length > 0;
    }
}

const NoviLogging = new NoviLoggingService();

export default NoviLogging;